<template>
  <v-data-table
    :headers="headers"
    :items="roles"
    :sort-by="[{ key: '#', order: 'asc' }]"
  >
    <!-- Top bar: search + New Sub-category -->
    <template v-slot:top>
      <v-toolbar flat class="bg-white px-4 py-3">
        <v-row align="center" justify="space-between" no-gutters>
          <!-- Search -->
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search"
              density="comfortable"
              variant="outlined"
              hide-details
              append-inner-icon="mdi-magnify"
              label="Search roles..."
              @input="getRoles"
            />
          </v-col>

          <!-- New Sub-category button -->
          <v-col cols="12" sm="4" class="text-right">
            <v-btn
              variant="elevated"
              color="blue"
              size="large"
              prepend-icon="mdi-plus-thick"
              class="rounded-pill px-6 font-semibold text-white"
              @click="dialog = true"
            >
              New Role
            </v-btn>
          </v-col>
        </v-row>
      </v-toolbar>
    </template>

    <!-- Row numbering -->
    <template v-slot:[`item.id`]="{ index }">
      {{ index + 1 }}
    </template>

    <!-- Description fallback -->
    <template v-slot:[`item.description`]="{ item }">
      {{ item.description || "N/A" }}
    </template>

    <!-- Edit/Delete actions -->
    <template v-slot:[`item.actions`]="{ item }">
      <v-btn
        icon
        size="small"
        variant="flat"
        color="blue"
        @click="editItem(item)"
      >
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
      <v-btn
        icon
        size="small"
        variant="flat"
        color="error"
        @click="deleteItem(item)"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </template>

    <!-- No-data slot -->
    <template #no-data>
      <v-card flat class="text-center pa-4">
        <v-icon size="36" color="grey">mdi-database-remove</v-icon>
        <div class="text-subtitle-2 mt-2">No roles found</div>
        <v-btn color="black" class="mt-2" @click="getRoles"> Reset </v-btn>
      </v-card>
    </template>
  </v-data-table>

  <!-- Add/Edit Dialog -->
  <v-dialog v-model="dialog" max-width="600px">
    <v-card>
      <v-card-title class="text-h6">{{ formTitle }}</v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="isValid" @submit.prevent="save">
          <v-row dense>
            <v-col cols="12">
              <v-text-field
                v-model="editedItem.title"
                label="Title"
                :rules="rules.title"
                dense
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editedItem.slug"
                label="Slug"
                :rules="rules.slug"
                dense
              />
            </v-col>
          
            <v-col cols="12">
              <v-textarea
                v-model="editedItem.description"
                label="Description"
                dense
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" color="grey" @click="close">Cancel</v-btn>
        <v-btn
          variant="elevated"
          color="black"
          class="rounded-pill px-6 font-semibold text-white"
          :disabled="!isValid"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete Confirmation -->
  <v-dialog v-model="dialogDelete" max-width="400px">
    <v-card>
      <v-card-title class="text-h6 text-center">Confirm Deletion</v-card-title>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn variant="text" color="grey" @click="closeDelete">Cancel</v-btn>
        <v-btn
          variant="elevated"
          color="error"
          class="rounded-pill px-6 font-semibold text-white"
          @click="deleteItemConfirm"
        >
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import RolesService from "../services/role.services.js";
export default {
  data: () => ({
    dialog: false,
    dialogDelete: false,
    isValid: false,
    rules: {
      title: [
        (value) => {
          if (value?.length >= 3) return true;
          return "You must enter a title.";
        },
      ],
      slug: [
        (value) => {
          if (value?.length >= 3) return true;
          return "You must enter a slug.";
        },
      ],
    },
    headers: [
      {
        title: "#",
        value: "id",
        align: "start",
        sortable: false,
        key: "id",
      },
      { title: "Title", value: "title", key: "title" },
      { title: "Slug", value: "slug", key: "slug" },
      // { title: "Description", value: "description", key: "description" },
      { title: "Actions", key: "actions", sortable: false },
    ],

    editedIndex: -1,
    roles: [],
    editedItem: {
      title: "",
      slug: "",
      description: "",
    },
    defaultItem: {
      title: "",
      slug: "",
      description: "",
    },
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Role" : "Edit Role";
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },

  created() {
    this.getRoles();
  },

  methods: {
    async validate() {
      this.isValid = await this.$refs.form.validate();
    },

    handleLogout() {
      this.$store.commit("logout");
      this.$store.dispatch("resetInactivityTimeout");
      this.$store.dispatch("auth/logout");
      this.$store.dispatch("resetInactivityTimeout");
      this.$router.push("/login");
      this.$store.dispatch("alert/success", "Logout Successfully");
    },

    getRoles() {
      return RolesService.index().then(
        (response) => {
          if (response.status == 200) {
            this.roles = response.data.data;
            this.loading = false;
          } else {
            this.roles = [];
            this.loading = false;
            console.log(response.data.console.error);
          }
        },
        (error) => {
          this.roles = [];
          if (error.response.status == 401) {
            this.handleLogout();
          }
          console.log(error);
          this.$store.dispatch("alert/error", error.message);
        }
      );
    },

    editItem(item) {
      this.editedIndex = this.roles.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteItem(item) {
      this.editedIndex = this.roles.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      RolesService.delete(this.editedItem).then(
        (response) => {
          if (response.status == 200) {
            this.$store.dispatch("alert/success", response.data.message);
            this.roles.splice(this.editedIndex, 1);
            this.closeDelete();
          } else {
            this.$store.dispatch("alert/error", response.data.message);
          }
        },
        (error) => {
          if (error.response.status == 422) {
            this.$store.dispatch("alert/error", error.response.data.message);
          }
          console.log(error);
          this.$store.dispatch("alert/error", error.response.data.message);
        }
      );
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    save() {
      if (this.validate()) {
        if (this.editedIndex > -1) {
          Object.assign(this.roles[this.editedIndex], this.editedItem);
          RolesService.update(this.editedItem).then(
            (response) => {
              if (response.status == 200) {
                this.$store.dispatch("alert/success", response.data.message);
                this.close();
                this.loading = false;
              } else {
                this.$store.dispatch("alert/error", response.data.message);
                this.loading = false;
              }
            },
            (error) => {
              if (error.response.status == 422) {
                this.$store.dispatch(
                  "alert/error",
                  error.response.data.message
                );
                this.loading = false;
              }
              console.log(error);
              this.$store.dispatch("alert/error", error.response.data.message);
            }
          );
        } else {
          RolesService.store(this.editedItem).then(
            (response) => {
              if (response.status == 201) {
                this.roles.push(this.editedItem);
                this.$store.dispatch("alert/success", response.data.message);
                this.loading = false;
                this.close();
              } else {
                this.$store.dispatch("alert/error", response.data.message);
                this.loading = false;
              }
            },
            (error) => {
              if (error.response.status == 422) {
                this.$store.dispatch(
                  "alert/error",
                  error.response.data.message
                );
                this.loading = false;
              }
              console.log(error);
              this.$store.dispatch("alert/error", error.response.data.message);
            }
          );
        }
      }
    },
  },
};
</script>
